import { useState } from 'react'
import styled from 'styled-components'
import { GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME } from '../../constants'
import { useGetSupportedTypes } from '../../services/api/settings.hooks'
import { useGetUserInfo } from '../../services/api/user-info.hooks'
import { Border, Colors, Shadows, Spacing, Typography } from '../../styles'
import Spinner from '../atoms/Spinner'
import SettingsModal from '../molecules/SettingsModal'

const Container = styled.div<{ isFullHeight?: boolean; minHeight?: number }>`
    background-color: inherit;
    box-sizing: border-box;
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${Border.radius.medium};
    width: 100%;
    padding: ${Spacing._8};
    ${Typography.body.medium};
    :hover {
        box-shadow: ${Shadows.deprecated_light};
        background-color: ${Colors.background.white};
        border-color: ${Colors.background.border};
    }
`
const DivCursorPointer = styled.div`
    cursor: pointer;
`

const TaskToCal = () => {
    return (
        <Container>
            <strong>Welcome to General Task!</strong> To help you get started, we’ve put together a list of introductory
            tasks.
            <p>
                <strong>Task-to-Calendar</strong> can help you set aside time for each of your tasks. If you’d like to
                set aside time to work on something, just <strong>drag a task over to the calendar sidebar</strong> on
                the right. This will also add it to your Google Calendar.
            </p>
            <p>
                ✅ Click the checkbox to mark this task as complete whenever you feel ready. Next up, we’ll take a look
                at Focus Mode.
            </p>
            <img width="100%" src="/images/nux-task-to-cal.png" />
        </Container>
    )
}

const FocusMode = () => {
    return (
        <Container>
            <strong>Focus Mode</strong> helps you stay on track by focusing on one task at a time.
            <p>
                You can <strong>enter Focus Mode from the navigation bar on the left</strong> or by pressing the{' '}
                <strong>F key</strong>. From here, Focus Mode will spotlight whichever tasks or events you have planned
                at that time. You can even join meetings with just one click. If you don’t have anything on your
                calendar, Focus Mode will present you a few options to help you decide what to work on next.
            </p>
            <p>
                <strong>Pro tip:</strong> You can pin the tab in your browser, or keep it on a second monitor for easy
                access to your task and event details throughout the day. Give it a try and see how it works for you.
            </p>
            <img src="/images/nux-focus-mode.png" width="100%" />
        </Container>
    )
}

const IntegrationsStaticContent = ({
    googleUrl,
    setIsSettingsModalOpen,
}: {
    googleUrl: string
    setIsSettingsModalOpen: (isSettingsModalOpen: boolean) => void
}) => (
    <>
        With General Task, you can <strong>connect Google Calendar</strong> to plan your work around your day.
        <p>
            📅 <a href={googleUrl}>Google Calendar</a>: easily schedule tasks onto your calendar.
        </p>
        <p>
            <DivCursorPointer onClick={() => setIsSettingsModalOpen(true)}>
                You can manage your connected Google account in settings.
            </DivCursorPointer>
        </p>
    </>
)

const Integrations = () => {
    const { data: supportedTypes } = useGetSupportedTypes()
    const googleUrl =
        supportedTypes?.find((type) => type.name === GOOGLE_CALENDAR_SUPPORTED_TYPE_NAME)?.authorization_url || ''
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

    return (
        <Container>
            <IntegrationsStaticContent
                googleUrl={googleUrl}
                setIsSettingsModalOpen={setIsSettingsModalOpen}
            />
            <SettingsModal isOpen={isSettingsModalOpen} setIsOpen={setIsSettingsModalOpen} />
        </Container>
    )
}

const JohnsLetterStaticContent = () => (
    <>
        <p>
            Thank you for choosing General Task! We know that there are many productivity solutions on the market, and
            we are truly honored that you landed on us.
        </p>
        <p>
            We have spent the last year building what we believe is a focused productivity workspace for software
            engineers. General Task brings your tasks and calendar into one place so you can plan your day and keep
            work moving.
        </p>
        <p>
            Productivity is close to our hearts. Our team has worked at some of the most successful companies in the
            industry: Meta, Netflix, LinkedIn, Robinhood, as well as many other smaller companies. We learned a lot and
            had a good time, but we also saw the downside of working at such companies: pointless meetings, constant
            distractions and waiting way too long to get your code reviewed, among many other issues.
        </p>
        <p>
            We know how crappy it can feel to have an unproductive day and how awesome it can feel to be super
            productive day. We want to make those awesome days happen all the time. That’s why we built this. We use
            General Task in-house daily, and we are already seeing substantial improvements in our own productivity. It
            feels great. We hope you enjoy the tool as much as we do!
        </p>
        <p>
            <strong>But we can’t do this alone!</strong>
        </p>
        <p>
            We are committed to listening to each and every one of your ideas, and will work tirelessly to improve our
            software as much as possible, as fast as possible. Please send us any bugs you notice, or product ideas
            you’d like to see in the app. Anything helps!
        </p>
        <p>
            Reach out to us with any questions, comments, or concerns at{' '}
            <a href="mailto:julian@generaltask.com">julian@generaltask.com</a> or{' '}
            <a href="mailto:john@generaltask.com">john@generaltask.com</a>.
        </p>
        <p>Happy building!</p>
        <p>Julian and John</p>
    </>
)

const JohnsLetter = () => {
    const { data: userInfo } = useGetUserInfo()
    if (!userInfo) return <Spinner />
    const firstName = userInfo.name.split(' ')[0]
    return (
        <Container>
            Hi {firstName},
            <JohnsLetterStaticContent />
        </Container>
    )
}

const PlanningTask = () => {
    return (
        <Container>
            General Task works best when your Task Inbox reflects the work you intend to plan.
            <ul>
                <li>
                    Add a task for anything that needs focused time.
                </li>
                <li>
                    Drag it onto your calendar when you know when you want to work on it.
                </li>
                <li>
                    Mark it done when the work is finished.
                </li>
            </ul>
            <img width="100%" src="/images/nux-task-to-cal.png" />
        </Container>
    )
}

interface NUXTaskBodyProps {
    nux_number_id: number
    renderSettingsModal?: boolean
}
const NUXTaskBody = ({ nux_number_id }: NUXTaskBodyProps) => {
    switch (nux_number_id) {
        case 1:
            return <TaskToCal />
        case 2:
            return <FocusMode />
        case 3:
            return <Integrations />
        case 4:
            return <JohnsLetter />
        case 5:
            return <PlanningTask />
        default:
            return null
    }
}

export const NuxTaskBodyStatic = ({ nux_number_id, renderSettingsModal }: NUXTaskBodyProps) => {
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
    switch (nux_number_id) {
        case 1:
            return <TaskToCal />
        case 2:
            return <FocusMode />
        case 3:
            return (
                <>
                    {renderSettingsModal && (
                        <SettingsModal isOpen={isSettingsModalOpen} setIsOpen={setIsSettingsModalOpen} />
                    )}
                    <IntegrationsStaticContent
                        googleUrl="https://api.generaltask.com/link/google/"
                        setIsSettingsModalOpen={setIsSettingsModalOpen}
                    />
                </>
            )
        case 4:
            return <JohnsLetterStaticContent />
        case 5:
            return <PlanningTask />
        default:
            return null
    }
}

export default NUXTaskBody
